import { AxiosError } from "axios";
import { UseQueryOptions, useQuery } from "react-query";

import { ITagGroup, ITagGroupsGetQueryParameters, Route } from "@edgetier/watchtower-types";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";
import { formatUnderscoreName } from "utilities/format-underscore-name";

/**
 * Get all tag groups and sort them alphabetically.
 * @param url Tag groups URL.
 * @returns   Tag groups sorted by name.
 */
const getTagGroups = async (url: string, parameters: ITagGroupsGetQueryParameters) => {
    const tagGroups = await getAllPaginatedItems<ITagGroup>(url, { params: parameters });
    return tagGroups
        .map((tagGroup) => ({ ...tagGroup, tagGroup: formatUnderscoreName(tagGroup.tagGroup) }))
        .sort((one, two) => one.tagGroup.localeCompare(two.tagGroup));
};

/**
 * Get all tag groups. By default these are cached for a long time as they will very rarely change.
 * @returns React Query result.
 */
const useTagGroups = <T = ITagGroup[]>(
    parameters: ITagGroupsGetQueryParameters = {},
    configuration: UseQueryOptions<ITagGroup[], AxiosError<void>, T> = {}
) => {
    const url = Route.TagGroups;
    return useQuery<ITagGroup[], AxiosError<void>, T>([url, parameters], () => getTagGroups(url, parameters), {
        cacheTime: Infinity,
        staleTime: Infinity,
        ...configuration,
    });
};

export default useTagGroups;
